import { combineLatest, map } from 'rxjs';
import { isTraversalLoadedState, LoadedState, ViewIds } from '@ardoq/api-types';
import { anyType } from '@ardoq/global-consts';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  hasNamedExpansion,
  hasTraversalPaths,
} from 'loadedState/isTraversalWithPaths';
import { loadedState$ } from 'loadedState/loadedState$';
import { isViewEligibleForNewAssets } from 'views/environmentals/restrictedViews';
import { type ModeViews, viewpointModeViews$ } from 'views/modeViews$';
import { pathOperations } from 'traversals/pathOperations';
import { i18nLocale$ } from './i18nLocale$';
import { activeView$ } from './views/mainContent/activeView$';

export type SaveAsViewpointButtonState = {
  isDisabled: boolean;
  disabledDescription: string | undefined;
  addToDashboardDisabledDescription: string | undefined;
};

const hasWildcardStartSet = (loadedState: LoadedState[]): boolean =>
  loadedState.some(
    state =>
      isTraversalLoadedState(state) &&
      !hasNamedExpansion(state.data) &&
      pathOperations.getStartTypeFromPaths(state.data.paths) === anyType
  );

type DisabledDescriptionArgs = {
  isViewConformant: boolean;
  canSaveViewpoint: boolean;
  hasWildcardStart: boolean;
  hasSavableDataset: boolean;
  i18nLocale: I18nLocale;
};

const getDisabledDescription = ({
  isViewConformant,
  canSaveViewpoint,
  hasWildcardStart,
  hasSavableDataset,
  i18nLocale,
}: DisabledDescriptionArgs): string | undefined => {
  if (!isViewConformant) {
    return t(
      i18nLocale,
      'appContainer:saveDropdown.viewpointDisabledViewNotConform',
      'You cannot save this view as a viewpoint. Please try switching to a different view.'
    );
  }
  if (canSaveViewpoint) {
    return undefined;
  }
  if (hasWildcardStart) {
    return t(
      i18nLocale,
      'appContainer:saveDropdown.viewpointDisabledWildcardStartSet',
      "A start set of 'Any Type' can not be saved as viewpoint."
    );
  }
  if (hasSavableDataset) {
    return t(
      i18nLocale,
      'appContainer:saveDropdown.viewpointDisabledMultipleDatasets',
      "You can't save as Viewpoint with more than 1 dataset opened. Please close some datasets first."
    );
  }
  return t(
    i18nLocale,
    'appContainer:saveDropdown.viewpointDisabledNoReferences',
    'You can only save as Viewpoint when you open a dataset with references added or descendants included.'
  );
};

const getAddToDashboardDescription = ({
  isViewConformant,
  canSaveViewpoint,
  hasWildcardStart,
  hasSavableDataset,
  i18nLocale,
}: DisabledDescriptionArgs): string | undefined => {
  if (!isViewConformant) {
    return t(
      i18nLocale,
      'appContainer:saveDropdown.dashboardDisabledViewNotConform',
      'You cannot add this view to a dashboard. Please try switching to a different view.'
    );
  }
  if (canSaveViewpoint) {
    return undefined;
  }
  if (hasWildcardStart) {
    return t(
      i18nLocale,
      'appContainer:saveDropdown.dashboardDisabledWildcardStartSet',
      "A start set of 'Any Type' can not be added to a dashboard."
    );
  }
  if (hasSavableDataset) {
    return t(
      i18nLocale,
      'appContainer:saveDropdown.dashboardDisabledMultipleDatasets',
      "You can't add this view to a dashboard with more than 1 dataset opened. Please close some datasets first."
    );
  }
  return t(
    i18nLocale,
    'appContainer:saveDropdown.dashboardDisabledNoReferences',
    'You can only add this view to a dashboard when you open a dataset with references added or descendants included.'
  );
};

export const buildSaveAsViewpointButtonState = (
  loadedState: LoadedState[],
  currentViewId: ViewIds,
  viewpointModeViewsById: ModeViews<ViewIds>['byIds'],
  i18nLocale: I18nLocale
): SaveAsViewpointButtonState => {
  const hasSavableDataset = loadedState.some(
    state =>
      isTraversalLoadedState(state) &&
      (hasNamedExpansion(state.data) || hasTraversalPaths(state.data.paths))
  );
  const hasWildcardStart = hasWildcardStartSet(loadedState);
  const canSaveViewpoint =
    hasSavableDataset && loadedState.length === 1 && !hasWildcardStart;
  const isViewConformant = isViewEligibleForNewAssets(
    viewpointModeViewsById[currentViewId]
  );

  return {
    isDisabled: !canSaveViewpoint || !isViewConformant,
    disabledDescription: getDisabledDescription({
      isViewConformant,
      canSaveViewpoint,
      hasWildcardStart,
      hasSavableDataset,
      i18nLocale,
    }),
    addToDashboardDisabledDescription: getAddToDashboardDescription({
      isViewConformant,
      canSaveViewpoint,
      hasWildcardStart,
      hasSavableDataset,
      i18nLocale,
    }),
  };
};

export const saveAsViewpointButtonState$ = combineLatest([
  loadedState$,
  activeView$,
  viewpointModeViews$,
  i18nLocale$,
]).pipe(
  map(([{ loadedStates }, { mainViewId }, { byIds }, i18nLocale]) =>
    buildSaveAsViewpointButtonState(loadedStates, mainViewId, byIds, i18nLocale)
  )
);
